import { Injectable, inject } from '@angular/core';
import { StorageService } from './storage/storage.service';
import { LoggerService } from '../../../shared/services/logger.service';

export interface ConsentSettings {
  analytics: boolean;
  necessary: boolean; // Always true, cannot be disabled
}

export interface UserConsent {
  hasConsented: boolean;
  consentDate?: string;
  settings: ConsentSettings;
}

@Injectable({
  providedIn: 'root',
})
export class ConsentService {
  private storageService = inject(StorageService);
  private loggerService = inject(LoggerService);

  private defaultConsent: UserConsent = {
    hasConsented: true,
    consentDate: new Date().toISOString(),
    settings: {
      analytics: true,
      necessary: true,
    },
  };

  private readonly CONSENT_KEY = 'user_consent';

  async getUserConsent(): Promise<UserConsent | null> {
    try {
      let consent = await this.storageService.get<UserConsent>(this.CONSENT_KEY);

      if (!consent) {
        this.saveUserConsent({
          analytics: true,
          necessary: true,
        });

        consent = await this.storageService.get<UserConsent>(this.CONSENT_KEY);
      }

      return consent;
    } catch (ex) {
      console.error(ex);
    }

    return this.defaultConsent;
  }

  async saveUserConsent(settings: ConsentSettings): Promise<void> {
    const consent: UserConsent = {
      hasConsented: true,
      consentDate: new Date().toISOString(),
      settings,
    };

    await this.storageService.save(this.CONSENT_KEY, consent);

    // Update logger service with consent settings
    this.loggerService.updateConsentSettings(settings);
  }

  async canTrackAnalytics(): Promise<boolean> {
    const consent = await this.getUserConsent();
    return consent?.settings.analytics || false;
  }

  async clearConsent(): Promise<void> {
    await this.storageService.remove(this.CONSENT_KEY);
  }
}
